import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import "../screenstyles/AssignUserSerials.css";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import {
  CheckCircleOutline,
  CloudUpload,
  Download,
  PlayArrow,
  Search,
  Undo,
} from "@mui/icons-material";

import {
  assignSerialsToUser,
  undoSerialsFromUser,
  checkUserExists,
  ExcelSerialRow,
  SerialProcessResult,
  AssignSerialsSummary,
  UndoSerialsSummary,
} from "../services/assignUserSerials";

interface ParsedExcelData {
  validRows: ExcelSerialRow[];
  invalidRows: SerialProcessResult[];
  duplicatedRows: SerialProcessResult[];
  totalRows: number;
}

const emptySummary: AssignSerialsSummary = {
  total: 0,
  processed: 0,
  success: 0,
  moved: 0,
  notFound: 0,
  alreadyUsed: 0,
  alreadyInUser: 0,
  productMismatch: 0,
  invalid: 0,
  failed: 0,
  addedPoints: 0,
  returnSuccess: 0,
returnNotFound: 0,
returnWrongUser: 0,
removedPoints: 0,
};

const emptyUndoSummary: UndoSerialsSummary = {
  total: 0,
  processed: 0,

  success: 0,

  restored: 0,
  removed: 0,

  usedNotFound: 0,
  wrongUser: 0,
  userSerialNotFound: 0,
  sourceConflict: 0,

  failed: 0,

  removedPoints: 0,
};

const normalizeHeader = (value: unknown): string => {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");
};

const getRowValue = (
  row: Record<string, unknown>,
  acceptedHeaders: string[]
): unknown => {
  const normalizedHeaders = acceptedHeaders.map(normalizeHeader);

  const foundKey = Object.keys(row).find((key) =>
    normalizedHeaders.includes(normalizeHeader(key))
  );

  return foundKey ? row[foundKey] : undefined;
};

const normalizeSerial = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number") {
    return value.toLocaleString("fullwide", {
      useGrouping: false,
      maximumFractionDigits: 0,
    });
  }

  return String(value)
    .trim()
    .replace(/\s+/g, "")
    .replace(/\.0$/, "");
};

const normalizePoints = (value: unknown): number => {
  if (typeof value === "number") {
    return value;
  }

  return Number(
    String(value ?? "")
      .trim()
      .replace(/,/g, "")
  );
};

const AssignUserSerials: React.FC = () => {
  const [userId, setUserId] = useState("");
  const [note, setNote] = useState(
    "تمت إضافة السيريال بواسطة لوحة التحكم"
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [parsedData, setParsedData] =
    useState<ParsedExcelData | null>(null);

  const [results, setResults] = useState<SerialProcessResult[]>([]);
  const [summary, setSummary] =
    useState<AssignSerialsSummary>(emptySummary);

  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState<boolean | null>(null);

  const [readingFile, setReadingFile] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [undoing, setUndoing] = useState(false);

const [undoSummary, setUndoSummary] =
  useState<UndoSerialsSummary>(
    emptyUndoSummary
  );
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");

  const progressPercent =
    progressTotal > 0
      ? Math.round((progressCurrent / progressTotal) * 100)
      : 0;

  const displayedResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return results;
    }

    return results.filter((result) => {
      return (
        result.serialNumber.toLowerCase().includes(query) ||
        result.productId.toLowerCase().includes(query) ||

        (result.movementType ?? "")
  .toLowerCase()
  .includes(query) ||
        result.message.toLowerCase().includes(query) ||
        result.status.toLowerCase().includes(query)
      );
    });
  }, [results, searchQuery]);

const resetExecution = () => {
  setResults([]);

  setSummary(emptySummary);
  setUndoSummary(emptyUndoSummary);

  setProgressCurrent(0);
  setProgressTotal(0);
};

  const handleUserIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUserId(event.target.value);
    setUserExists(null);
    resetExecution();
  };

  const handleCheckUser = async () => {
    const normalizedUserId = userId.trim();

    if (!normalizedUserId) {
      toast.error("يرجى إدخال معرف المستخدم");
      return;
    }

    try {
      setCheckingUser(true);

      const exists = await checkUserExists(normalizedUserId);

      setUserExists(exists);

      if (exists) {
        toast.success("تم العثور على المستخدم");
      } else {
        toast.error("المستخدم غير موجود داخل كولكشن users");
      }
    } catch (error) {
      console.error("User check error:", error);
      setUserExists(false);
      toast.error("حدث خطأ أثناء التحقق من المستخدم");
    } finally {
      setCheckingUser(false);
    }
  };

  const parseExcelFile = async (
    file: File
  ): Promise<ParsedExcelData> => {
    const arrayBuffer = await file.arrayBuffer();

    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
      cellDates: true,
      raw: false,
    });

    const firstSheetName = workbook.SheetNames[0];

    if (!firstSheetName) {
      throw new Error("ملف Excel لا يحتوي على صفحات");
    }

    const worksheet = workbook.Sheets[firstSheetName];

    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
      worksheet,
      {
        defval: "",
        raw: false,
      }
    );

    const validRows: ExcelSerialRow[] = [];
    const invalidRows: SerialProcessResult[] = [];
    const duplicatedRows: SerialProcessResult[] = [];

    const seenSerials = new Set<string>();

    rows.forEach((row, index) => {
      const rowNumber = index + 2;

      const serialNumber = normalizeSerial(
        getRowValue(row, [
          "serial",
          "serialNumber",
          "serial_number",
          "serial number",
          "السيريال",
          "رقم السيريال",
        ])
      );

      const productId = String(
        getRowValue(row, [
          "productId",
          "product_id",
          "product id",
          "itemId",
          "item_id",
          "معرف المنتج",
          "ايدي المنتج",
        ]) ?? ""
      ).trim();

      const points = normalizePoints(
        getRowValue(row, [
          "point",
          "points",
          "النقاط",
          "عدد النقاط",
        ])
      );

      const movementType = String(
  getRowValue(row, [
    "movementType",
    "movement_type",
    "movement type",
    "نوع الحركة",
    "نوع الحركه",
  ]) ?? ""
).trim();

      if (!serialNumber) {
        invalidRows.push({
          serialNumber: "",
          productId,
          points: Number.isFinite(points) ? points : 0,
          rowNumber,
          status: "invalid",
          message: "رقم السيريال غير موجود",
        });

        return;
      }

      if (serialNumber.includes("/")) {
        invalidRows.push({
          serialNumber,
          productId,
          points: Number.isFinite(points) ? points : 0,
          rowNumber,
          status: "invalid",
          message:
            "رقم السيريال يحتوي على / ولا يمكن استخدامه كمعرف دكيومنت",
        });

        return;
      }

      if (!productId) {
        invalidRows.push({
          serialNumber,
          productId,
          points: Number.isFinite(points) ? points : 0,
          rowNumber,
          status: "invalid",
          message: "معرف المنتج productId غير موجود",
        });

        return;
      }

      if (!Number.isFinite(points) || points <= 0) {
        invalidRows.push({
          serialNumber,
          productId,
          points: Number.isFinite(points) ? points : 0,
          rowNumber,
          status: "invalid",
          message: "قيمة النقاط غير صحيحة أو أقل من واحد",
        });

        return;
      }

      if (seenSerials.has(serialNumber)) {
        duplicatedRows.push({
          serialNumber,
          productId,
          points,
          rowNumber,
          status: "invalid",
          message: "السيريال مكرر داخل ملف Excel",
        });

        return;
      }
         if (
      movementType !== "مبيعات" &&
      movementType !== "مردودات مبيعات"
    ) {
      invalidRows.push({
        serialNumber,
        productId,
        points: Number.isFinite(points) ? points : 0,
        rowNumber,
        status: "invalid",
        message:
          "نوع الحركة يجب أن يكون مبيعات أو مردودات مبيعات",
      });

      return;
    }

    if (seenSerials.has(serialNumber)) {
      duplicatedRows.push({
        serialNumber,
        productId,
        points,
        movementType: movementType as
          | "مبيعات"
          | "مردودات مبيعات",
        rowNumber,
        status: "invalid",
        message: "السيريال مكرر داخل ملف Excel",
      });

      return;
    }

    seenSerials.add(serialNumber);

    validRows.push({
      serialNumber,
      productId,
      points,
      movementType: movementType as
        | "مبيعات"
        | "مردودات مبيعات",
      rowNumber,
    });
  });

  return {
    validRows,
    invalidRows,
    duplicatedRows,
    totalRows: rows.length,
  };
};
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const extension = file.name
      .split(".")
      .pop()
      ?.toLowerCase();

    if (!["xlsx", "xls"].includes(extension ?? "")) {
      toast.error("يرجى اختيار ملف Excel بصيغة xlsx أو xls");
      event.target.value = "";
      return;
    }

    try {
      setReadingFile(true);
      setSelectedFile(file);
      resetExecution();

      const parsed = await parseExcelFile(file);

      setParsedData(parsed);

      const previewResults = [
        ...parsed.invalidRows,
        ...parsed.duplicatedRows,
      ];

      setResults(previewResults);

      toast.success(
        `تمت قراءة ${parsed.totalRows} صف، والصالح منها ${parsed.validRows.length}`
      );
    } catch (error) {
      console.error("Excel parsing error:", error);

      setSelectedFile(null);
      setParsedData(null);

      toast.error(
        error instanceof Error
          ? error.message
          : "حدث خطأ أثناء قراءة ملف Excel"
      );
    } finally {
      setReadingFile(false);
      event.target.value = "";
    }
  };

  const handleExecute = async () => {
    const normalizedUserId = userId.trim();

    if (!normalizedUserId) {
      toast.error("يرجى إدخال معرف المستخدم");
      return;
    }

    if (!parsedData) {
      toast.error("يرجى رفع ملف Excel أولًا");
      return;
    }

    if (parsedData.validRows.length === 0) {
      toast.error("لا توجد صفوف صالحة للتنفيذ");
      return;
    }

    try {
      setProcessing(true);
      setProgressCurrent(0);
      setProgressTotal(parsedData.validRows.length);

      const exists = await checkUserExists(normalizedUserId);

      setUserExists(exists);

      if (!exists) {
        toast.error("المستخدم غير موجود داخل كولكشن users");
        return;
      }

      const executionResult = await assignSerialsToUser(
        normalizedUserId,
        parsedData.validRows,
        {
          note,
          onProgress: (current, total) => {
            setProgressCurrent(current);
            setProgressTotal(total);
          },
        }
      );

      const finalResults: SerialProcessResult[] = [
        ...executionResult.results,
        ...parsedData.invalidRows,
        ...parsedData.duplicatedRows,
      ];

      setResults(finalResults);

      setSummary({
        ...executionResult.summary,
        total: parsedData.totalRows,
        invalid:
          executionResult.summary.invalid +
          parsedData.invalidRows.length +
          parsedData.duplicatedRows.length,
      });

      toast.success(
        `اكتملت العملية. تمت إضافة ${executionResult.summary.success} سيريال بنجاح`
      );
    } catch (error) {
      console.error("Assign serials error:", error);

      toast.error(
        error instanceof Error
          ? error.message
          : "حدث خطأ أثناء تنفيذ العملية"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleUndo = async () => {
  const normalizedUserId =
    userId.trim();

  if (!normalizedUserId) {
    toast.error(
      "يرجى إدخال معرف المستخدم"
    );
    return;
  }

  if (!parsedData) {
    toast.error(
      "يرجى رفع ملف Excel الذي تريد التراجع عن سيريلاته"
    );
    return;
  }

  if (
    parsedData.validRows.length === 0
  ) {
    toast.error(
      "لا توجد سيريلات صالحة للتراجع"
    );
    return;
  }

  const confirmed = window.confirm(
    `تحذير!\n\n` +
      `سيتم محاولة عكس العملية لـ ${parsedData.validRows.length} سيريال.\n\n` +
      `سيقوم النظام بـ:\n` +
      `- حذف السيريالات من المستخدم\n` +
      `- خصم النقاط المرتبطة بها\n` +
      `- حذفها من UsedSerialNumbers\n` +
      `- إعادة السيريلات الأصلية إلى SerailNumber عند الحاجة\n\n` +
      `لن يتم حذف أي سيريال يعود لمستخدم آخر.\n\n` +
      `هل أنت متأكد؟`
  );

  if (!confirmed) {
    return;
  }

  try {
    setUndoing(true);

    setSummary(emptySummary);
    setUndoSummary(emptyUndoSummary);

    setProgressCurrent(0);
    setProgressTotal(
      parsedData.validRows.length
    );

    const exists =
      await checkUserExists(
        normalizedUserId
      );

    setUserExists(exists);

    if (!exists) {
      toast.error(
        "المستخدم غير موجود داخل كولكشن users"
      );
      return;
    }

    const executionResult =
      await undoSerialsFromUser(
        normalizedUserId,
        parsedData.validRows,
        {
          onProgress: (
            current,
            total
          ) => {
            setProgressCurrent(
              current
            );

            setProgressTotal(
              total
            );
          },
        }
      );

    const finalResults: SerialProcessResult[] =
      [
        ...executionResult.results,
        ...parsedData.invalidRows,
        ...parsedData.duplicatedRows,
      ];

    setResults(finalResults);

    setUndoSummary(
      executionResult.summary
    );

    toast.success(
      `اكتملت عملية التراجع. تم عكس ${executionResult.summary.success} سيريال بنجاح`
    );
  } catch (error) {
    console.error(
      "Undo serials error:",
      error
    );

    toast.error(
      error instanceof Error
        ? error.message
        : "حدث خطأ أثناء عكس العملية"
    );
  } finally {
    setUndoing(false);
  }
};

  const handleDownloadResults = () => {
    if (results.length === 0) {
      toast.error("لا توجد نتائج لتنزيلها");
      return;
    }

    const reportRows = results.map((result, index) => ({
      "#": index + 1,
      "رقم الصف": result.rowNumber ?? "",
      "رقم السيريال": result.serialNumber,
      "معرف المنتج": result.productId,
      النقاط: result.points,
      "نوع الحركة":
  result.movementType ?? "",
      الحالة: getStatusLabel(result.status),
      الرسالة: result.message,
      "كان موجودًا في SerailNumber":
        result.existedInSerailNumber === undefined
          ? ""
          : result.existedInSerailNumber
          ? "نعم"
          : "لا",
    }));

    const worksheet = XLSX.utils.json_to_sheet(reportRows);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Results"
    );

    XLSX.writeFile(
      workbook,
      `assign-user-serials-results-${Date.now()}.xlsx`
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        direction: "rtl",
        fontFamily: "Tajawal, sans-serif",
        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: "24px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2.5,
            color: "#ffffff",
            background:
              "linear-gradient(135deg, #111827 0%, #1f2937 55%, #3d7585 100%)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Tajawal, sans-serif",
              fontWeight: 900,
              fontSize: {
                xs: 20,
                md: 26,
              },
            }}
          >
            إضافة سيريلات ونقاط إلى مستخدم
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              color: "#cbd5e1",
              fontFamily: "Tajawal, sans-serif",
              fontSize: 14,
            }}
          >
            رفع ملف Excel، مقارنة السيريلات، نقلها إلى
            UsedSerialNumbers وإضافتها إلى نقاط المستخدم.
          </Typography>
        </Box>

        <Box
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "1fr auto",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <TextField
              label="معرف المستخدم User ID"
              value={userId}
              onChange={handleUserIdChange}
              fullWidth
              disabled={processing}
              InputProps={{
                sx: {
                  fontFamily: "Tajawal, sans-serif",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Tajawal, sans-serif",
                },
              }}
            />

            <Button
              variant="outlined"
              onClick={handleCheckUser}
              disabled={checkingUser || processing}
              startIcon={
                checkingUser ? (
                  <CircularProgress size={18} />
                ) : (
                  <CheckCircleOutline />
                )
              }
              sx={{
                minHeight: 56,
                px: 3,
                borderRadius: "14px",
                fontFamily: "Tajawal, sans-serif",
                fontWeight: 800,
              }}
            >
              التحقق من المستخدم
            </Button>
          </Box>

          {userExists !== null && (
            <Box
              sx={{
                mt: 1.5,
                px: 2,
                py: 1.2,
                borderRadius: "12px",
                fontFamily: "Tajawal, sans-serif",
                color: userExists ? "#166534" : "#991b1b",
                background: userExists ? "#dcfce7" : "#fee2e2",
                border: userExists
                  ? "1px solid #86efac"
                  : "1px solid #fecaca",
              }}
            >
              {userExists
                ? "المستخدم موجود ويمكن تنفيذ العملية."
                : "المستخدم غير موجود داخل كولكشن users."}
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            <Box
              component="label"
              sx={{
                minHeight: 150,
                borderRadius: "20px",
                border: "2px dashed #94a3b8",
                background: "#f8fafc",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor:
                  processing || readingFile
                    ? "not-allowed"
                    : "pointer",
                transition: "0.2s",
                "&:hover": {
                  borderColor: "#3d7585",
                  background: "#f1f5f9",
                },
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                disabled={processing || readingFile}
                onChange={handleFileChange}
              />

              {readingFile ? (
                <CircularProgress />
              ) : (
                <CloudUpload
                  sx={{
                    fontSize: 44,
                    color: "#3d7585",
                  }}
                />
              )}

              <Typography
                sx={{
                  mt: 1,
                  fontFamily: "Tajawal, sans-serif",
                  fontWeight: 800,
                }}
              >
                اختر ملف Excel
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: "Tajawal, sans-serif",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                الأعمدة المطلوبة: serial - productId - point
              </Typography>

              {selectedFile && (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: "Tajawal, sans-serif",
                    color: "#0f766e",
                    fontWeight: 700,
                  }}
                >
                  {selectedFile.name}
                </Typography>
              )}
            </Box>

            <TextField
              label="ملاحظة العملية"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              minRows={5}
              disabled={processing}
              fullWidth
              InputProps={{
                sx: {
                  fontFamily: "Tajawal, sans-serif",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Tajawal, sans-serif",
                },
              }}
            />
          </Box>

          {parsedData && (
            <Box
              sx={{
                mt: 3,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <SummaryCard
                title="مجموع الصفوف"
                value={parsedData.totalRows}
              />

              <SummaryCard
                title="الصفوف الصالحة"
                value={parsedData.validRows.length}
              />

              <SummaryCard
                title="الصفوف غير الصالحة"
                value={parsedData.invalidRows.length}
              />

              <SummaryCard
                title="المكرر داخل الملف"
                value={parsedData.duplicatedRows.length}
              />
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              variant="contained"
              onClick={handleExecute}
              disabled={
                processing ||
                !parsedData ||
                parsedData.validRows.length === 0 ||
                !userId.trim()
              }
              startIcon={
                processing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PlayArrow />
                )
              }
              sx={{
                px: 4,
                py: 1.4,
                borderRadius: "14px",
                fontFamily: "Tajawal, sans-serif",
                fontWeight: 900,
                background: "#3d7585",
                boxShadow: "none",
                "&:hover": {
                  background: "#315f6c",
                  boxShadow: "none",
                },
              }}
            >
              {processing
                ? "جاري تنفيذ العملية..."
                : "تنفيذ نقل وإضافة السيريلات"}
            </Button>

<Button
  variant="contained"
  color="error"
  onClick={handleUndo}
  disabled={
    processing ||
    undoing ||
    !parsedData ||
    parsedData.validRows.length === 0 ||
    !userId.trim()
  }
  startIcon={
    undoing ? (
      <CircularProgress
        size={18}
        color="inherit"
      />
    ) : (
      <Undo />
    )
  }
  sx={{
    px: 4,
    py: 1.4,
    borderRadius: "14px",
    fontFamily:
      "Tajawal, sans-serif",
    fontWeight: 900,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  }}
>
  {undoing
    ? "جاري عكس العملية..."
    : "عكس العملية وإزالة السيريلات"}
</Button>
            <Button
              variant="outlined"
              onClick={handleDownloadResults}
              disabled={results.length === 0 || processing}
              startIcon={<Download />}
              sx={{
                px: 3,
                borderRadius: "14px",
                fontFamily: "Tajawal, sans-serif",
                fontWeight: 800,
              }}
            >
              تنزيل تقرير النتائج
            </Button>
          </Box>

          {(processing || progressTotal > 0) && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: "16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Tajawal, sans-serif",
                    fontWeight: 800,
                  }}
                >
                  تقدم العملية
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Tajawal, sans-serif",
                  }}
                >
                  {progressCurrent} / {progressTotal} —{" "}
                  {progressPercent}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 10,
                  borderRadius: 99,
                  backgroundColor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#3d7585",
                  },
                }}
              />
            </Box>
          )}

          {summary.processed > 0 && (
            <Box
              sx={{
                mt: 3,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <SummaryCard
                title="تمت إضافتها"
                value={summary.success}
              />

              <SummaryCard
                title="نُقلت من SerailNumber"
                value={summary.moved}
              />

              <SummaryCard
                title="غير موجودة في المصدر"
                value={summary.notFound}
              />

              <SummaryCard
                title="النقاط المضافة"
                value={summary.addedPoints}
              />

<SummaryCard
  title="مردودات المبيعات المنفذة"
  value={summary.returnSuccess}
/>

<SummaryCard
  title="مردودات غير موجودة لدى المستخدم"
  value={summary.returnNotFound}
/>

<SummaryCard
  title="مردودات تعود لمستخدم آخر"
  value={summary.returnWrongUser}
/>

<SummaryCard
  title="النقاط المخصومة"
  value={summary.removedPoints}
/>
              <SummaryCard
                title="مستخدمة مسبقًا"
                value={summary.alreadyUsed}
              />

              <SummaryCard
                title="موجودة لدى المستخدم"
                value={summary.alreadyInUser}
              />

              <SummaryCard
                title="اختلاف المنتج"
                value={summary.productMismatch}
              />

              <SummaryCard
                title="فشل أو بيانات غير صحيحة"
                value={summary.failed + summary.invalid}
              />
            </Box>
          )}

{undoSummary.processed > 0 && (
  <Box
    sx={{
      mt: 3,
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, 1fr)",
        md: "repeat(4, 1fr)",
      },
      gap: 1.5,
    }}
  >
    <SummaryCard
      title="تم التراجع عنها"
      value={undoSummary.success}
    />

    <SummaryCard
      title="أُعيدت إلى SerailNumber"
      value={undoSummary.restored}
    />

    <SummaryCard
      title="حُذفت بدون إرجاع"
      value={undoSummary.removed}
    />

    <SummaryCard
      title="النقاط المخصومة"
      value={
        undoSummary.removedPoints
      }
    />

    <SummaryCard
      title="غير موجودة في UsedSerialNumbers"
      value={
        undoSummary.usedNotFound
      }
    />

    <SummaryCard
      title="تعود لمستخدم آخر"
      value={undoSummary.wrongUser}
    />

    <SummaryCard
      title="غير موجودة عند المستخدم"
      value={
        undoSummary.userSerialNotFound
      }
    />

    <SummaryCard
      title="تعارض في SerailNumber"
      value={
        undoSummary.sourceConflict
      }
    />

    <SummaryCard
      title="فشل"
      value={undoSummary.failed}
    />
  </Box>
)}
          {results.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  justifyContent: "space-between",
                  alignItems: {
                    xs: "stretch",
                    md: "center",
                  },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Tajawal, sans-serif",
                    fontWeight: 900,
                    fontSize: 18,
                  }}
                >
                  نتائج المعالجة
                </Typography>

                <TextField
                  size="small"
                  placeholder="البحث في النتائج..."
                  value={searchQuery}
                  onChange={(event) =>
                    setSearchQuery(event.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Search
                        sx={{
                          ml: 1,
                          color: "#64748b",
                        }}
                      />
                    ),
                    sx: {
                      fontFamily: "Tajawal, sans-serif",
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "16px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 950,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#111827",
                        color: "#ffffff",
                      }}
                    >
                      <TableHeader>#</TableHeader>
                      <TableHeader>رقم الصف</TableHeader>
                      <TableHeader>السيريال</TableHeader>
                      <TableHeader>Product ID</TableHeader>
                      <TableHeader>النقاط</TableHeader>
                      <TableHeader>نوع الحركة</TableHeader>

                      <TableHeader>الحالة</TableHeader>
                      <TableHeader>التفاصيل</TableHeader>
                    </tr>
                  </thead>

                  <tbody>
                    {displayedResults.map((result, index) => (
                      <tr
                        key={`${result.serialNumber}-${result.rowNumber}-${index}`}
                        style={{
                          borderBottom:
                            "1px solid #e2e8f0",
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>

                        <TableCell>
                          {result.rowNumber ?? "-"}
                        </TableCell>

                        <TableCell>
                          {result.serialNumber || "-"}
                        </TableCell>

                        <TableCell>
                          {result.productId || "-"}
                        </TableCell>

                        <TableCell>
                          {result.points}
                        </TableCell>

                        <TableCell>
  {result.movementType ?? "-"}
</TableCell>

                        <TableCell>
                          <StatusBadge
                            status={result.status}
                          />
                        </TableCell>

                        <TableCell>
                          {result.message}
                        </TableCell>
                      </tr>
                    ))}

                    {displayedResults.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            padding: 30,
                            textAlign: "center",
                            fontFamily:
                              "Tajawal, sans-serif",
                          }}
                        >
                          لا توجد نتائج مطابقة للبحث
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

interface SummaryCardProps {
  title: string;
  value: number;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
}) => {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "16px",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Tajawal, sans-serif",
          color: "#64748b",
          fontSize: 13,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontFamily: "Tajawal, sans-serif",
          fontWeight: 900,
          fontSize: 24,
          color: "#0f172a",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const TableHeader: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <th
      style={{
        padding: "14px 12px",
        textAlign: "center",
        fontFamily: "Tajawal, sans-serif",
        fontSize: 13,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
};

const TableCell: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <td
      style={{
        padding: "12px",
        textAlign: "center",
        fontFamily: "Tajawal, sans-serif",
        fontSize: 13,
        color: "#334155",
      }}
    >
      {children}
    </td>
  );
};

const getStatusLabel = (
  status: SerialProcessResult["status"]
): string => {
  switch (status) {
    case "success_moved":
      return "تم النقل والإضافة";

    case "success_not_found":
      return "تمت الإضافة دون وجود المصدر";

    case "already_used":
      return "مستخدم مسبقًا";

    case "already_in_user":
      return "موجود لدى المستخدم";

    case "product_mismatch":
      return "اختلاف المنتج";

    case "invalid":
      return "بيانات غير صحيحة";

    case "failed":
      return "فشل";
case "undo_restored":
  return "تم التراجع والإرجاع";

case "undo_removed":
  return "تم التراجع والحذف";

case "undo_used_not_found":
  return "غير موجود في UsedSerialNumbers";

case "undo_wrong_user":
  return "يعود لمستخدم آخر";

case "undo_user_serial_not_found":
  return "غير موجود لدى المستخدم";

case "undo_source_conflict":
  return "تعارض في SerailNumber";

  case "return_success":
  return "تم مردود المبيعات";

case "return_not_found":
  return "غير موجود لدى المستخدم";

case "return_wrong_user":
  return "السيريال لمستخدم آخر";
    default:
      return status;
  }
};

const getStatusColors = (
  status: SerialProcessResult["status"]
): {
  background: string;
  color: string;
} => {
  switch (status) {
    case "success_moved":
      return {
        background: "#dcfce7",
        color: "#166534",
      };

    case "success_not_found":
      return {
        background: "#dbeafe",
        color: "#1d4ed8",
      };

    case "already_used":
    case "already_in_user":
      return {
        background: "#fef3c7",
        color: "#92400e",
      };

    case "product_mismatch":
      return {
        background: "#ffedd5",
        color: "#c2410c",
      };

    case "invalid":
    case "failed":
      return {
        background: "#fee2e2",
        color: "#991b1b",
      };
case "undo_restored":
  return {
    background: "#dcfce7",
    color: "#166534",
  };

case "undo_removed":
  return {
    background: "#dbeafe",
    color: "#1d4ed8",
  };

case "undo_used_not_found":
case "undo_user_serial_not_found":
  return {
    background: "#fef3c7",
    color: "#92400e",
  };

case "undo_wrong_user":
case "undo_source_conflict":
  return {
    background: "#fee2e2",
    color: "#991b1b",
  };

  case "return_success":
  return {
    background: "#dcfce7",
    color: "#166534",
  };

case "return_not_found":
  return {
    background: "#fef3c7",
    color: "#92400e",
  };

case "return_wrong_user":
  return {
    background: "#fee2e2",
    color: "#991b1b",
  };
    default:
      return {
        background: "#e2e8f0",
        color: "#334155",
      };
  }
};

const StatusBadge: React.FC<{
  status: SerialProcessResult["status"];
}> = ({ status }) => {
  const colors = getStatusColors(status);

  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 999,
        background: colors.background,
        color: colors.color,
        fontFamily: "Tajawal, sans-serif",
        fontWeight: 800,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {getStatusLabel(status)}
    </span>
  );
};

export default AssignUserSerials;